import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SiteShell } from "@/components/site/SiteShell";
import { Section } from "@/components/site/Blocks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

type Search = { redirect?: string };
type Mode = "signin" | "signup" | "magic" | "forgot";

export const Route = createFileRoute("/auth")({
  ssr: false,
  validateSearch: (s: Record<string, unknown>): Search => ({
    redirect: typeof s.redirect === "string" ? s.redirect : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Loan Officer Sign In | Simply Approved Mortgages" },
      { name: "description", content: "Secure sign-in for Simply Approved Mortgages loan officers and staff." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AuthPage,
});

const TABS: { key: Mode; label: string }[] = [
  { key: "signin", label: "Password" },
  { key: "magic", label: "Email link" },
  { key: "forgot", label: "Forgot password" },
];

function AuthPage() {
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: "/auth" }) as Search;
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: redirect ?? "/admin" });
    });
  }, [navigate, redirect]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setSent(null);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin + "/auth" },
        });
        if (error) throw error;
        toast.success("Account created. An admin must grant loan officer access.");
      } else if (mode === "magic") {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/auth${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`,
          },
        });
        if (error) throw error;
        setSent(`We emailed a secure sign-in link to ${email}. It expires shortly — open it on this device.`);
        toast.success("Sign-in link sent");
      } else if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setSent(`If an account exists for ${email}, a password reset link is on its way.`);
        toast.success("Reset email sent");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate({ to: redirect ?? "/admin" });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Request failed");
    } finally {
      setLoading(false);
    }
  }

  const heading =
    mode === "signup"
      ? "Create account"
      : mode === "magic"
        ? "Sign in with an email link"
        : mode === "forgot"
          ? "Reset your password"
          : "Loan officer sign in";

  const cta =
    mode === "signup"
      ? "Create account"
      : mode === "magic"
        ? "Email me a sign-in link"
        : mode === "forgot"
          ? "Send reset link"
          : "Sign in";

  return (
    <SiteShell hideNewsletter hideBreadcrumbs>
      <Section>
        <div className="mx-auto w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8">
          <h1 className="text-2xl font-black tracking-tight">{heading}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Internal tools for Simply Approved Mortgages staff. New accounts require role approval.
          </p>

          {mode !== "signup" && (
            <div className="mt-5 grid grid-cols-3 gap-1 rounded-lg bg-muted p-1" role="tablist">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={mode === t.key}
                  onClick={() => {
                    setMode(t.key);
                    setSent(null);
                  }}
                  className={`rounded-md px-2 py-2 text-xs font-semibold transition ${
                    mode === t.key ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {sent ? (
            <div className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm">{sent}</div>
          ) : null}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {(mode === "signin" || mode === "signup") && (
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete={mode === "signin" ? "current-password" : "new-password"}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? "Please wait…" : cta}
            </Button>
          </form>

          <button
            type="button"
            className="mt-4 text-sm text-brand underline"
            onClick={() => {
              setMode(mode === "signup" ? "signin" : "signup");
              setSent(null);
            }}
          >
            {mode === "signup" ? "Already have an account? Sign in" : "Need an account? Sign up"}
          </button>
        </div>
      </Section>
    </SiteShell>
  );
}
